import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from '@/contexts/CookieConsentContext';

const GA_MEASUREMENT_ID = 'G-YD50Y1V0TR';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

// No region is excluded from analytics tracking.
const shouldTrackVisitor = (): boolean => true;

export const useGoogleAnalytics = () => {
  const location = useLocation();
  const { cookiePreferences, hasConsent } = useCookieConsent();
  const [isAllowedRegion] = useState(() => shouldTrackVisitor());

  // Load GA4 script only when user consents to analytics cookies AND is from allowed region
  useEffect(() => {
    if (!hasConsent || !cookiePreferences.analytics || !isAllowedRegion) {
      return;
    }

    // Check if script is already loaded
    if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
      return;
    }

    // Load gtag.js script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
    });
  }, [hasConsent, cookiePreferences.analytics, isAllowedRegion]);

  // Track page views on route changes
  useEffect(() => {
    if (!hasConsent || !cookiePreferences.analytics || !isAllowedRegion || !window.gtag) {
      return;
    }

    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: location.pathname + location.search,
    });
  }, [location, hasConsent, cookiePreferences.analytics, isAllowedRegion]);
};

// Helper function to track custom events
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, unknown>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};
